import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Search, PackageX, MapPin, CalendarClock, Clock, Loader2, ShieldQuestion } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { fmtDate, fmtDateTime } from "@/lib/statuses";
import { Header } from "@/components/Header";
import { StatusBadge } from "@/components/StatusBadge";
import { Timeline } from "@/components/Timeline";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";

export default function PublicTrack() {
  const [params, setParams] = useSearchParams();
  const [value, setValue] = useState(params.get("t") || "");
  const [result, setResult] = useState(null);
  const [state, setState] = useState("idle"); // idle | loading | found | notfound | error
  const [error, setError] = useState("");

  useEffect(() => {
    const initial = params.get("t");
    if (initial) doSearch(initial);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const doSearch = async (tn) => {
    const q = (tn ?? value).trim();
    if (!q) return;
    setValue(q);
    setParams({ t: q });
    setState("loading");
    setResult(null);
    setError("");
    try {
      const { data } = await api.get(`/track/${encodeURIComponent(q)}`);
      setResult(data);
      setState("found");
    } catch (e) {
      if (e.response?.status === 404) setState("notfound");
      else {
        setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        setState("error");
      }
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />

      <section className="relative overflow-hidden border-b border-slate-200 bg-slate-900">
        <div
          className="absolute inset-0 opacity-[0.07]"
          style={{
            backgroundImage:
              "linear-gradient(to right, #fff 1px, transparent 1px), linear-gradient(to bottom, #fff 1px, transparent 1px)",
            backgroundSize: "48px 48px",
          }}
        />
        <div className="relative mx-auto max-w-4xl px-4 py-14 text-center sm:px-6 sm:py-20">
          <motion.p
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-sm font-semibold uppercase tracking-widest text-orange-400"
          >
            Global Shipment Tracking
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 }}
            className="font-heading mt-3 text-4xl font-bold tracking-tight text-white sm:text-5xl lg:text-6xl"
          >
            Track your shipment
          </motion.h1>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.12 }}
            className="mx-auto mt-4 max-w-xl text-base text-slate-300"
          >
            Enter your TransitLine Express tracking number to see live status and full delivery history.
          </motion.p>

          <motion.form
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.18 }}
            onSubmit={(e) => {
              e.preventDefault();
              doSearch();
            }}
            className="mx-auto mt-8 flex max-w-xl flex-col gap-3 sm:flex-row"
          >
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
              <Input
                data-testid="public-tracking-input"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                placeholder="e.g. TLX-88234-US"
                className="h-12 border-0 bg-white pl-10 font-mono text-base text-slate-900 shadow-lg"
              />
            </div>
            <Button
              type="submit"
              data-testid="public-tracking-submit-btn"
              className="h-12 bg-orange-600 px-8 text-base font-semibold hover:bg-orange-700"
            >
              {state === "loading" ? <Loader2 className="h-5 w-5 animate-spin" /> : "Track"}
            </Button>
          </motion.form>
        </div>
      </section>

      <main className="mx-auto max-w-4xl px-4 py-10 sm:px-6">
        {state === "notfound" && (
          <Card data-testid="tracking-not-found" className="flex flex-col items-center gap-3 p-10 text-center">
            <PackageX className="h-12 w-12 text-slate-300" />
            <h2 className="font-heading text-xl font-semibold text-slate-900">No shipment found</h2>
            <p className="max-w-md text-sm text-slate-500">
              We couldn't find a shipment for{" "}
              <span className="font-mono font-medium text-slate-700">{value}</span>. Double-check the number and
              try again.
            </p>
          </Card>
        )}

        {state === "error" && (
          <Card data-testid="tracking-error" className="p-6 text-center text-sm text-red-600">
            {error}
          </Card>
        )}

        {state === "found" && result && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
            <Card className="overflow-hidden">
              <div className="flex flex-col gap-4 border-b border-slate-100 p-6 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  {result.is_demo && (
                    <span className="mb-2 inline-block rounded bg-slate-100 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                      Demo shipment
                    </span>
                  )}
                  <p className="font-mono text-sm text-slate-500">{result.tracking_number}</p>
                  <h2 className="font-heading mt-1 text-2xl font-bold text-slate-900">{result.description}</h2>
                  <p className="mt-1 text-sm text-slate-500">{result.service_level} service</p>
                </div>
                <StatusBadge status={result.status} />
              </div>

              <div className="grid grid-cols-1 gap-px bg-slate-100 sm:grid-cols-3">
                <div className="bg-white p-5">
                  <p className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-slate-400">
                    <MapPin className="h-3.5 w-3.5" /> Route
                  </p>
                  <p className="mt-1.5 text-sm font-medium text-slate-800">
                    {result.origin.city}, {result.origin.country}
                  </p>
                  <p className="text-xs text-slate-400">to</p>
                  <p className="text-sm font-medium text-slate-800">
                    {result.destination.city}, {result.destination.country}
                  </p>
                </div>
                <div className="bg-white p-5">
                  <p className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-slate-400">
                    <CalendarClock className="h-3.5 w-3.5" /> Estimated Delivery
                  </p>
                  <p className="mt-1.5 text-sm font-medium text-slate-800">
                    {result.estimated_delivery ? fmtDate(result.estimated_delivery) : "Not available yet"}
                  </p>
                  {!result.estimated_delivery && (
                    <p className="mt-1 flex items-center gap-1 text-xs text-slate-400">
                      <ShieldQuestion className="h-3.5 w-3.5" /> Pending customs / update
                    </p>
                  )}
                </div>
                <div className="bg-white p-5">
                  <p className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-slate-400">
                    <Clock className="h-3.5 w-3.5" /> Last Update
                  </p>
                  <p className="mt-1.5 text-sm font-medium text-slate-800">{fmtDateTime(result.last_update)}</p>
                </div>
              </div>
            </Card>

            <Card className="p-6">
              <h3 className="font-heading mb-5 text-lg font-semibold text-slate-900">Shipment history</h3>
              <Timeline events={result.events} />
            </Card>

            <p className="text-center text-xs text-slate-400">
              For your privacy, only tracking details are shown here. Contact us for delivery address changes.
            </p>
          </motion.div>
        )}
      </main>
    </div>
  );
}
